import { useEffect, useMemo, useState } from "react";
import type { Deal, Status } from "@/types/deal";
import {
  loadPipelinePreferences,
  savePipelinePreferences,
} from "./pipelinePreferences";
import type { PipelineViewId } from "./pipelineViews";
import { isMyRow, type BandOverrides } from "./pipelineMeeting";

export function usePipelineFilters(
  deals: Deal[],
  userId?: number | string | null,
) {
  const [initialPreferences] = useState(loadPipelinePreferences);
  const [view, setView] = useState<PipelineViewId>(initialPreferences.view);
  const [customColumns, setCustomColumns] = useState<string[]>(
    initialPreferences.customColumns,
  );
  // Not saved: leaving the Pipeline always turns editing off, so coming
  // back needs a deliberate click on the pencil.
  const [editing, setEditing] = useState(false);
  const [myDealsOnly, setMyDealsOnly] = useState(
    initialPreferences.myDealsOnly,
  );
  // Expanded/collapsed status bands for this visit only: the Meeting view
  // always opens with Closed groups collapsed and the rest open.
  const [bandOverrides, setBandOverrides] = useState<BandOverrides>({});
  const [filter, setFilter] = useState("");
  // True while the grid shows only deals in an active status.
  const [activeOnly, setActiveOnly] = useState(initialPreferences.activeOnly);
  const [statusFilter, setStatusFilter] = useState<Status[]>(
    initialPreferences.statusFilter,
  );
  const [countryFilterIds, setCountryFilterIds] = useState<(string | number)[]>(
    initialPreferences.countryFilterIds,
  );
  const [sectorFilterIds, setSectorFilterIds] = useState<(string | number)[]>(
    initialPreferences.sectorFilterIds,
  );
  const [capitalSourceFilterIds, setCapitalSourceFilterIds] = useState<
    (string | number)[]
  >(initialPreferences.capitalSourceFilterIds);
  const [sourceFilterIds, setSourceFilterIds] = useState<(string | number)[]>(
    initialPreferences.sourceFilterIds,
  );

  useEffect(() => {
    savePipelinePreferences({
      view,
      customColumns,
      activeOnly,
      myDealsOnly,
      statusFilter,
      countryFilterIds,
      sectorFilterIds,
      capitalSourceFilterIds,
      sourceFilterIds,
    });
  }, [
    view,
    customColumns,
    activeOnly,
    myDealsOnly,
    statusFilter,
    countryFilterIds,
    sectorFilterIds,
    capitalSourceFilterIds,
    sourceFilterIds,
  ]);

  const filteredRows = useMemo(
    () =>
      deals
        .filter((deal) => {
          const selectedCountryIds = new Set(countryFilterIds.map(String));
          const selectedSectorIds = new Set(sectorFilterIds.map(String));
          const selectedCapitalSourceIds = new Set(
            capitalSourceFilterIds.map(String),
          );
          const selectedSourceIds = new Set(sourceFilterIds.map(String));

          const dealCountryIds = (deal.countries ?? []).map((c) =>
            String(c.id),
          );
          const dealSectorIds = (deal.sectors ?? []).map((s) => String(s.id));
          const dealCapitalSourceId =
            deal.capital_source?.id != null
              ? String(deal.capital_source.id)
              : null;
          // Add-on rows carry their parent deal's source.
          const dealSourceId = deal.source_id ?? deal.source_entity?.id;

          const matchesName = deal.deal_name
            ?.toLowerCase()
            .includes(filter.toLowerCase());
          const matchesStatus =
            statusFilter.length === 0 ||
            statusFilter.some((s) => s.id === deal.status?.id);
          const matchesCountry =
            selectedCountryIds.size === 0 ||
            dealCountryIds.some((id) => selectedCountryIds.has(id));
          const matchesSector =
            selectedSectorIds.size === 0 ||
            dealSectorIds.some((id) => selectedSectorIds.has(id));
          const matchesCapitalSource =
            selectedCapitalSourceIds.size === 0 ||
            (dealCapitalSourceId != null &&
              selectedCapitalSourceIds.has(dealCapitalSourceId));
          const matchesSource =
            selectedSourceIds.size === 0 ||
            (dealSourceId != null &&
              selectedSourceIds.has(String(dealSourceId)));

          const matchesMine = !myDealsOnly || isMyRow(deal, userId);

          return (
            matchesMine &&
            matchesName &&
            matchesStatus &&
            matchesCountry &&
            matchesSector &&
            matchesCapitalSource &&
            matchesSource
          );
        })
        .sort((a, b) => {
          const aSortOrder = a.status?.sort_order ?? Number.MAX_SAFE_INTEGER;
          const bSortOrder = b.status?.sort_order ?? Number.MAX_SAFE_INTEGER;
          return aSortOrder - bSortOrder;
        }),
    [
      deals,
      filter,
      statusFilter,
      countryFilterIds,
      sectorFilterIds,
      capitalSourceFilterIds,
      sourceFilterIds,
      myDealsOnly,
      userId,
    ],
  );

  return {
    view,
    setView,
    customColumns,
    setCustomColumns,
    editing,
    setEditing,
    myDealsOnly,
    setMyDealsOnly,
    bandOverrides,
    setBandOverrides,
    filter,
    setFilter,
    activeOnly,
    setActiveOnly,
    statusFilter,
    setStatusFilter,
    countryFilterIds,
    setCountryFilterIds,
    sectorFilterIds,
    setSectorFilterIds,
    capitalSourceFilterIds,
    setCapitalSourceFilterIds,
    sourceFilterIds,
    setSourceFilterIds,
    filteredRows,
  };
}
